/**
 * Receipt-to-referral rules for the XREF01 outbox.
 * Communicate stores the XML. This module only decides whether a loaded
 * XCTL belongs to one outbox row, and which of the three statuses it is.
 */

export type OutboundReferralStatus = 'built' | 'xctl01' | 'xctl02' | 'xctl03';

export type RefuseCode =
  | 'ukendt_lokationsnummer'
  | 'problem_med_version'
  | 'problem_med_modtagerID'
  | 'syntaksfejl'
  | 'ikke_specificeret';

const REFUSE_CODES: readonly RefuseCode[] = [
  'ukendt_lokationsnummer',
  'problem_med_version',
  'problem_med_modtagerID',
  'syntaksfejl',
  'ikke_specificeret',
];

export type AttachmentReason = 'matched' | 'mismatch' | 'doublet' | 'unreadable';

export type AttachmentDecision = {
  update: boolean;
  status: OutboundReferralStatus;
  reason: AttachmentReason;
};

export function isOutboundReferralStatus(value: string): value is OutboundReferralStatus {
  switch (value) {
    case 'built':
    case 'xctl01':
    case 'xctl02':
    case 'xctl03':
      return true;
    default:
      return false;
  }
}

export function statusFromReceiptKind(kind: string): OutboundReferralStatus | null {
  switch (kind) {
    case 'XCTL01':
      return 'xctl01';
    case 'XCTL02':
      return 'xctl02';
    case 'XCTL03':
      return 'xctl03';
    default:
      return null;
  }
}

/**
 * A receipt belongs to this referral only when both keys match:
 * the coupled outgoing exchange, and the kuvert the receipt cites.
 */
export function receiptMatchesReferral(input: {
  coupledToId: string | null;
  originalEnvelopeIdentifier: string | null;
  exchangeId: string;
  envelopeIdentifier: string;
}): boolean {
  if (input.coupledToId === null || input.originalEnvelopeIdentifier === null) return false;
  return (
    input.coupledToId === input.exchangeId &&
    input.originalEnvelopeIdentifier === input.envelopeIdentifier
  );
}

/**
 * How much a status weighs. Used only to order the patient summary
 * (negative first). The headline status of a referral is the latest receipt
 * received, see {@link effectiveReceipt}.
 */
export function receiptSeverity(status: OutboundReferralStatus): number {
  switch (status) {
    case 'built':
      return 0;
    case 'xctl03':
      return 1;
    case 'xctl01':
    case 'xctl02':
      return 2;
    default: {
      const unexpected: never = status;
      return unexpected;
    }
  }
}

export function isNegativeReceiptStatus(status: OutboundReferralStatus): boolean {
  return status === 'xctl01' || status === 'xctl02';
}

/**
 * Decision at the moment a file is loaded. The latest receipt received is the
 * referral's status ("latest wins"): MedCom REF02-S 2.1–2.3 loads XCTL01,
 * XCTL02 and then XCTL03 on the same sent referral and expects the positive
 * receipt to show; CTL-Receive 2.4 loads a positive and then a negative and
 * expects the negative. Both hold when the newest receipt decides. Earlier
 * receipts stay visible in the receipt history.
 *
 * A doublet is the same receipt loaded again. It is the newest receipt now,
 * so its status is recorded too; the reason tells the user it was a reload.
 */
export function decideReceiptAttachment(input: {
  coupledToId: string | null;
  originalEnvelopeIdentifier: string | null;
  exchangeId: string;
  envelopeIdentifier: string;
  doublet: boolean;
  currentStatus: OutboundReferralStatus;
  kind: string;
}): AttachmentDecision {
  const matches = receiptMatchesReferral(input);
  if (!matches) {
    return { update: false, status: input.currentStatus, reason: 'mismatch' };
  }
  const next = statusFromReceiptKind(input.kind);
  if (next === null) {
    return { update: false, status: input.currentStatus, reason: 'unreadable' };
  }
  return { update: true, status: next, reason: input.doublet ? 'doublet' : 'matched' };
}

export type ListedReceipt = {
  kind: string;
  originalEnvelopeIdentifier: string | null;
  /** When Communicate stored (received or imported) the receipt, ISO. */
  receivedAt?: string | null;
};

export type ReceiptStatus = Exclude<OutboundReferralStatus, 'built'>;

export type HistoryEntry<T> = { receipt: T; status: ReceiptStatus };

function receivedAtMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Every receipt for this referral, newest first: the receipts Communicate
 * holds for its kuvert that are coupled to this referral and are XCTL01,
 * XCTL02 or XCTL03. Receipts for another kuvert, or not coupled to this
 * referral, are left out. Ordered by receivedAt; receipts without a time keep
 * Communicate's order (newest first).
 */
export function receiptHistory<T extends ListedReceipt>(input: {
  receipts: readonly T[];
  coupledToId: string | null;
  exchangeId: string;
  envelopeIdentifier: string;
}): HistoryEntry<T>[] {
  const entries: { entry: HistoryEntry<T>; index: number }[] = [];
  input.receipts.forEach((receipt, index) => {
    const matches = receiptMatchesReferral({
      coupledToId: input.coupledToId,
      originalEnvelopeIdentifier: receipt.originalEnvelopeIdentifier,
      exchangeId: input.exchangeId,
      envelopeIdentifier: input.envelopeIdentifier,
    });
    if (!matches) return;
    const status = statusFromReceiptKind(receipt.kind);
    if (status === null || status === 'built') return;
    entries.push({ entry: { receipt, status }, index });
  });
  entries.sort((a, b) => {
    const am = receivedAtMs(a.entry.receipt.receivedAt);
    const bm = receivedAtMs(b.entry.receipt.receivedAt);
    if (am !== null && bm !== null && am !== bm) return bm - am;
    return a.index - b.index;
  });
  return entries.map((e) => e.entry);
}

/**
 * The receipt that decides a referral's status: the latest one received
 * ("latest wins", see {@link decideReceiptAttachment}). Null when there is none.
 */
export function effectiveReceipt<T extends ListedReceipt>(input: {
  receipts: readonly T[];
  coupledToId: string | null;
  exchangeId: string;
  envelopeIdentifier: string;
}): HistoryEntry<T> | null {
  return receiptHistory(input)[0] ?? null;
}

/**
 * Decision when the outbox is opened again, from the effective (latest)
 * receipt. Returns the status to store, or null when the stored one stays.
 */
export function statusFromReceiptList(input: {
  currentStatus: OutboundReferralStatus;
  effective: OutboundReferralStatus | null;
}): OutboundReferralStatus | null {
  const next = input.effective;
  if (next === null || next === input.currentStatus) return null;
  return next;
}

/** One receipt in the outbox row's history, as communicate-ctl returns it. */
export type ReceiptHistoryItem = {
  /** Communicate exchange id of the receipt; null when Communicate gave none. */
  id: string | null;
  kind: 'XCTL01' | 'XCTL02' | 'XCTL03';
  status: ReceiptStatus;
  /** When Communicate stored (received or imported) the receipt, ISO. */
  receivedAt: string | null;
  refuseCode: RefuseCode | null;
  refuseText: string | null;
  /** True for the latest receipt, the one that sets the row's status. */
  current: boolean;
};

const KIND_BY_STATUS: Record<ReceiptStatus, ReceiptHistoryItem['kind']> = {
  xctl01: 'XCTL01',
  xctl02: 'XCTL02',
  xctl03: 'XCTL03',
};

/** History entries (newest first) → the items the outbox shows. */
export function receiptHistoryItems(
  entries: readonly HistoryEntry<{
    id?: string | null;
    refuseCode?: string | null;
    refuseText?: string | null;
    receivedAt?: string | null;
  } & ListedReceipt>[],
): ReceiptHistoryItem[] {
  return entries.map((entry, index) => ({
    id: entry.receipt.id ?? null,
    kind: KIND_BY_STATUS[entry.status],
    status: entry.status,
    receivedAt: entry.receipt.receivedAt ?? null,
    refuseCode: entry.status === 'xctl03' ? null : knownRefuseCode(entry.receipt.refuseCode ?? null),
    refuseText: entry.status === 'xctl03' ? null : (entry.receipt.refuseText ?? null),
    current: index === 0,
  }));
}

export function knownRefuseCode(value: string | null): RefuseCode | null {
  if (value === null) return null;
  for (const code of REFUSE_CODES) {
    if (code === value) return code;
  }
  return null;
}

/** One ISO-8859-1 byte per character. A code point above 255 becomes `?`. */
export function latin1Bytes(text: string): Uint8Array {
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    bytes[i] = code > 255 ? 0x3f : code;
  }
  return bytes;
}

/**
 * C12 (BIN01-S 3.14): the attachment (XBIN02 companion) of an outbox row that a
 * receipt answers, by the outgoing exchange Communicate coupled it to.
 * Returns the companion's exchange id, or null when the receipt is not for one
 * of this row's attachments.
 */
export function companionForReceipt(
  coupledToId: string | null,
  companions: readonly { exchangeId: string }[],
): string | null {
  if (coupledToId === null) return null;
  return companions.some((c) => c.exchangeId === coupledToId) ? coupledToId : null;
}

/** Companion pointers stored on an outbox row (`companions` jsonb). */
export function readCompanionPointers(value: unknown): { exchangeId: string; fileName: string }[] {
  if (!Array.isArray(value)) return [];
  const out: { exchangeId: string; fileName: string }[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const exchangeId = typeof r.exchangeId === 'string' ? r.exchangeId : '';
    const fileName = typeof r.fileName === 'string' ? r.fileName : '';
    if (exchangeId && fileName) out.push({ exchangeId, fileName });
  }
  return out;
}
